import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Country, State, City } from "country-state-city";
import { ColorPicker } from "primereact/colorpicker";
import { Divider } from "primereact/divider";
import { FileUpload } from "primereact/fileupload";
import { Image } from "primereact/image";
import {
  getSetupDetails,
  updateSetupDetails,
} from "../../store/AsyncMethods/SetupMethod";
import { toast } from "react-toastify";
import { Menubar } from "primereact/menubar";

export default function SetupMain() {
  const dispatch = useDispatch();
  const countries = Country.getAllCountries();
  const [allStates, setStates] = useState([]);
  const [allCities, setCities] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [image, setImage] = useState(null);
  const fileUploadRef = useRef(null);

  const items = [
    {
      label: "Home",
      icon: "pi pi-home",
      command: () => {
        navigate("/site-setup");
      },
    },
    {
      label: "Hero Section",
      icon: "pi pi-palette",
      items: [
        {
          label: "Hero",
          icon: "pi pi-pencil",
          command: () => {
            navigate("/site-setup/hero-section");
          },
        },
        {
          label: "Coupons",
          icon: "pi pi-pencil",
          command: () => {
            navigate("/site-setup/coupons");
          },
        },
      ],
    },
    {
      label: "Contact Section",
      icon: "pi pi-phone",
      command: () => {
        navigate("/site-setup/contact-section");
      },
    },

  ];

  const navigate = useNavigate();
  const { setupDetails } = useSelector((state) => state.SetupReducer);
  useEffect(() => {
    if (setupDetails) {
      console.log(setupDetails);
    }
  }, [setupDetails]);

  const onFileUpload = () => {
    if (fileUploadRef.current) {
      const inputElement = fileUploadRef.current.getInput();
      inputElement?.click();
    }
  };

  const onFileSelect = (e) => {
    const file = e.files[0];

    if (!file) {
      toast.error("File is invalid or exceeding size");
      return;
    }

    if (
      file.type !== "image/png" &&
      file.type !== "image/jpeg" &&
      file.type !== "image/jpg" &&
      file.type !== "image/svg+xml"
    ) {
      toast.error("Only PNG, JPG, JPEG, and SVG files are allowed");
      return;
    }

    setImageUrl(file);

    const url = URL.createObjectURL(file);
    setImage(url);
  };

  const styleOptions = [
    { name: "Italic", id: "italic" },
    { name: "Non italic", id: "not-italic" },
  ];

  useEffect(() => {
    if (setupDetails) {
      formik.setValues({
        name: setupDetails.name || "",
        email: setupDetails.email || "",
        regNo: setupDetails.regNo || "12345",
        foundedYear: setupDetails.foundedYear || "2025",
        country: setupDetails.country || "",
        state: setupDetails.state || "",
        city: setupDetails.city || "",
        address: setupDetails.address || "",
        postalCode: setupDetails.postalCode || "",
        phone: setupDetails.phone || "03202020222",
        long: setupDetails.longi || "",
        lat: setupDetails.lat || "",
        colorPrimary: setupDetails.colorPrimary || "",
        colorSecondary: setupDetails.colorSecondary || "",
        fontStyle: setupDetails.fontStyle || "",
        website: setupDetails.website || "",
        description: setupDetails.description || "",
        // Social Media Links
        facebook: setupDetails.facebook || "",
        instagram: setupDetails.instagram || "",
        twitter: setupDetails.twitter || "",
        // Shipping Charges
        shipping_charges: (() => {
          if (typeof setupDetails.shipping_charges === "string") {
            try {
              return JSON.parse(setupDetails.shipping_charges);
            } catch (e) {
              return { single: "", multiple: "" };
            }
          }
          return setupDetails.shipping_charges || { single: "", multiple: "" };
        })(),
        // Meta Tags
        meta_title: setupDetails.meta_title || "",
        meta_description: setupDetails.meta_description || "",
        meta_keywords: setupDetails.meta_keywords || "",
        meta_author: setupDetails.meta_author || "",
        meta_robots: setupDetails.meta_robots || "",
        // Open Graph Tags
        og_title: setupDetails.og_title || "",
        og_description: setupDetails.og_description || "",
        og_type: setupDetails.og_type || "",
        og_url: setupDetails.og_url || "",
        og_site_name: setupDetails.og_site_name || "",
        // Twitter Tags
        twitter_card: setupDetails.twitter_card || "",
        twitter_title: setupDetails.twitter_title || "",
        twitter_description: setupDetails.twitter_description || "",
        // Schema.org
        schema_org: setupDetails.schema_org || "{}",
      });
    }
  }, [setupDetails]);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      regNo: "12345",
      foundedYear: "2025",
      country: "",
      state: "",
      city: "",
      address: "",
      postalCode: "",
      phone: "03003030300",
      long: "",
      lat: "",
      colorPrimary: "444444",
      colorSecondary: "454545",
      fontStyle: "",
      logo: "",
      website: "",
      description: "",
      // Social Media Links
      facebook: "",
      instagram: "",
      twitter: "",
      // Shipping Charges
      shipping_charges: { single: "", multiple: "" },
      // Meta Tags
      meta_title: "",
      meta_description: "",
      meta_keywords: "",
      meta_author: "",
      meta_robots: "",
      // Open Graph Tags
      og_title: "",
      og_description: "",
      og_type: "",
      og_url: "",
      og_site_name: "",
      // Twitter Tags
      twitter_card: "",
      twitter_title: "",
      twitter_description: "",
      // Schema.org
      schema_org: "{}",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name Required"),
      email: Yup.string().required("Email Required"),
      regNo: Yup.number().required("Registration No. Required"),
      foundedYear: Yup.number().required("Founded year Required"),
      country: Yup.string().required("Country Required"),
      state: Yup.string().required("State Required"),
      city: Yup.string().required("City Required"),
      address: Yup.string().required("Address Required"),
      postalCode: Yup.string().required("Postal Code Required"),
      phone: Yup.number().required("Phone Required"),
      long: Yup.string().required("Longitude Required"),
      lat: Yup.string().required("Latitude Required"),
      colorPrimary: Yup.string().required("Primary Color Required"),
      colorSecondary: Yup.string().required("Secondary Color Required"),
      fontStyle: Yup.string().required("Font Style Required"),
      website: Yup.string().required("Website Required"),
      description: Yup.string().required("Description Required"),
      facebook: Yup.string().url("Must be a valid URL").optional(),
      instagram: Yup.string().url("Must be a valid URL").optional(),
      twitter: Yup.string().url("Must be a valid URL").optional(),
      shipping_charges: Yup.object({
        single: Yup.number()
          .min(0, "Single shipping charges must be 0 or greater")
          .optional(),
        multiple: Yup.number()
          .min(0, "Multiple shipping charges must be 0 or greater")
          .optional(),
      }).optional(),
    }),

    onSubmit: async (data) => {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === "shipping_charges") {
          // Convert shipping charges object to JSON string for backend
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      });

      if (imageUrl) {
        formData.append("image", imageUrl);
      }

      dispatch(updateSetupDetails(formData)).then((success) => {
        if (success) {
          formik.resetForm();
          dispatch(getSetupDetails());
          navigate("/site-setup");
        }
      });
    },
  });

  useEffect(() => {
    dispatch(getSetupDetails());
  }, []);
  useEffect(() => {
    setStates(State.getStatesOfCountry(formik.values.country));
  }, [formik.values.country]);
  useEffect(() => {
    setCities(
      City.getCitiesOfState(formik.values.country, formik.values.state)
    );
  }, [formik.values.state]);

  return (
    <div className="mx-4">
      <Menubar model={items} />
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-3">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
              Company Details
            </span>
          </Divider>

          <div className="px-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="">
                  Name*
                </label>
                <span className=" w-full">
                  <InputText
                    id="name"
                    name="name"
                    maxLength={25}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.name && formik.errors?.name && (
                <div className="p-error">{formik.errors?.name}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="">
                  Email*
                </label>
                <span className=" w-full">
                  <InputText
                    id="email"
                    name="email"
                    maxLength={100}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.email && formik.errors?.email && (
                <div className="p-error">{formik.errors?.email}</div>
              )}
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="regNo" className="">
                  Registration Number*
                </label>
                <span className=" w-full">
                  <InputNumber
                    id="regNo"
                    name="regNo"
                    maxLength={20}
                    useGrouping={false}
                    inputClassName="p-primary-input"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.regNo}
                    onValueChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.regNo && formik.errors?.regNo && (
                <div className="p-error">{formik.errors?.regNo}</div>
              )}
            </div>

            <div className="flex gap-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="foundedYear" className="">
                  Founded Year*
                </label>
                <span className=" w-full">
                  <InputNumber
                    id="foundedYear"
                    name="foundedYear"
                    maxLength={4}
                    useGrouping={false}
                    min={1900}
                    max={2100}
                    inputClassName="p-primary-input"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.foundedYear}
                    onValueChange={formik.handleChange}
                  />
                </span>
                {formik.touched?.foundedYear && formik.errors?.foundedYear && (
                  <div className="p-error">{formik.errors?.foundedYear}</div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="website" className="">
                  Website*
                </label>
                <span className=" w-full">
                  <InputText
                    id="website"
                    name="website"
                    maxLength={100}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                  />
                </span>
                {formik.touched?.website && formik.errors?.website && (
                  <div className="p-error">{formik.errors?.website}</div>
                )}
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="description" className="">
                  Company Description*
                </label>
                <span className=" w-full">
                  <InputTextarea
                    id="description"
                    name="description"
                    rows={4}
                    maxLength={1000}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    placeholder="Enter company description..."
                  />
                </span>
                {formik.touched?.description && formik.errors?.description && (
                  <div className="p-error">{formik.errors?.description}</div>
                )}
              </div>
            </div>

            {/* Social Media Links */}
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="facebook" className="">
                  Facebook URL
                </label>
                <span className=" w-full">
                  <InputText
                    id="facebook"
                    name="facebook"
                    maxLength={200}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.facebook}
                    onChange={formik.handleChange}
                    placeholder="https://facebook.com/yourcompany"
                  />
                </span>
                {formik.touched?.facebook && formik.errors?.facebook && (
                  <div className="p-error">{formik.errors?.facebook}</div>
                )}
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="instagram" className="">
                  Instagram URL
                </label>
                <span className=" w-full">
                  <InputText
                    id="instagram"
                    name="instagram"
                    maxLength={200}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.instagram}
                    onChange={formik.handleChange}
                    placeholder="https://instagram.com/yourcompany"
                  />
                </span>
                {formik.touched?.instagram && formik.errors?.instagram && (
                  <div className="p-error">{formik.errors?.instagram}</div>
                )}
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="twitter" className="">
                  Twitter URL
                </label>
                <span className=" w-full">
                  <InputText
                    id="twitter"
                    name="twitter"
                    maxLength={200}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.twitter}
                    onChange={formik.handleChange}
                    placeholder="https://twitter.com/yourcompany"
                  />
                </span>
                {formik.touched?.twitter && formik.errors?.twitter && (
                  <div className="p-error">{formik.errors?.twitter}</div>
                )}
              </div>
            </div>

            {/* Shipping Charges */}
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="shipping_charges_single" className="">
                  Single Item Shipping Charges (PKR)
                </label>
                <span className=" w-full">
                  <InputNumber
                    id="shipping_charges_single"
                    name="shipping_charges.single"
                    mode="currency"
                    currency="PKR"
                    locale="en-PK"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.shipping_charges?.single}
                    onValueChange={(e) => {
                      formik.setFieldValue("shipping_charges.single", e.value);
                    }}
                    placeholder="300"
                  />
                </span>
                {formik.touched?.shipping_charges?.single &&
                  formik.errors?.shipping_charges?.single && (
                    <div className="p-error">
                      {formik.errors.shipping_charges.single}
                    </div>
                  )}
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="shipping_charges_multiple" className="">
                  Multiple Items Shipping Charges (PKR)
                </label>
                <span className=" w-full">
                  <InputNumber
                    id="shipping_charges_multiple"
                    name="shipping_charges.multiple"
                    mode="currency"
                    currency="PKR"
                    locale="en-PK"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    className="w-full text-lg p-primary-input"
                    value={formik.values.shipping_charges?.multiple}
                    onValueChange={(e) => {
                      formik.setFieldValue(
                        "shipping_charges.multiple",
                        e.value
                      );
                    }}
                    placeholder="450"
                  />
                </span>
                {formik.touched?.shipping_charges?.multiple &&
                  formik.errors?.shipping_charges?.multiple && (
                    <div className="p-error">
                      {formik.errors.shipping_charges.multiple}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="card shadow-md rounded-lg p-4 pt-1 mt-3">
            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Country Detail
              </span>
            </Divider>

            <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="country" className="">
                    Select Country*
                  </label>
                  <Dropdown
                    placeholder="Select"
                    id="country"
                    name="country"
                    maxLength={25}
                    className="!w-full text-lg p-primary-input"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    options={countries}
                    optionLabel="name"
                    optionValue="isoCode"
                    filter
                    pt={{
                      root: { className: "w-full" },
                      input: { className: "w-full p-primary-input" },
                      filterIcon: { className: "ml-1" },
                      filterInput: { className: "pl-6" },
                    }}
                  />
                </div>
                {formik.touched?.country && formik.errors?.country && (
                  <div className="p-error">{formik.errors?.country}</div>
                )}
              </div>

              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="state" className="">
                    Select State*
                  </label>
                  <Dropdown
                    placeholder="Select"
                    id="state"
                    name="state"
                    maxLength={25}
                    className="!w-full text-lg p-primary-input"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    options={allStates}
                    optionLabel="name"
                    optionValue="isoCode"
                    filter
                    pt={{
                      root: { className: "w-full" },
                      input: { className: "w-full p-primary-input" },
                      filterIcon: { className: "ml-1" },
                      filterInput: { className: "pl-6" },
                    }}
                  />
                </div>
                {formik.touched?.state && formik.errors?.state && (
                  <div className="p-error">{formik.errors?.state}</div>
                )}
              </div>

              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="city" className="">
                    Select City*
                  </label>
                  <Dropdown
                    placeholder="Select"
                    id="city"
                    name="city"
                    maxLength={25}
                    className="!w-full text-lg p-primary-input"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    options={allCities}
                    optionLabel="name"
                    optionValue="name"
                    filter
                    pt={{
                      root: { className: "w-full" },
                      input: { className: "w-full p-primary-input" },
                      filterIcon: { className: "ml-1" },
                      filterInput: { className: "pl-6" },
                    }}
                  />
                </div>
                {formik.touched?.city && formik.errors?.city && (
                  <div className="p-error">{formik.errors?.city}</div>
                )}
              </div>
            </div>

            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Contact Details
              </span>
            </Divider>

            <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="address" className="">
                    Address*
                  </label>
                  <span className=" w-full">
                    <InputText
                      id="address"
                      name="address"
                      maxLength={100}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                    />
                  </span>
                </div>
                {formik.touched?.address && formik.errors?.address && (
                  <div className="p-error">{formik.errors?.address}</div>
                )}
              </div>

              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="postalCode" className="">
                    Postal code*
                  </label>
                  <span className=" w-full">
                    <InputText
                      id="postalCode"
                      name="postalCode"
                      maxLength={20}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.postalCode}
                      onChange={formik.handleChange}
                    />
                  </span>
                </div>
                {formik.touched?.postalCode && formik.errors?.postalCode && (
                  <div className="p-error">{formik.errors?.postalCode}</div>
                )}
              </div>

              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="phone" className="">
                    Phone*
                  </label>
                  <span className=" w-full">
                    <InputText
                      id="phone"
                      name="phone"
                      maxLength={20}
                      inputClassName="p-primary-input"
                      className="w-full text-lg p-primary-input"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                    />
                  </span>
                </div>
                {formik.touched?.phone && formik.errors?.phone && (
                  <div className="p-error">{formik.errors?.phone}</div>
                )}
              </div>

              <div className="flex justify-between gap-2">
                <div className="flex flex-col w-full gap-2">
                  <label htmlFor="long" className="">
                    Longitude*
                  </label>
                  <span className=" w-full">
                    <InputText
                      id="long"
                      name="long"
                      maxLength={30}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.long}
                      onChange={formik.handleChange}
                    />
                  </span>
                </div>
                {formik.touched?.long && formik.errors?.long && (
                  <div className="p-error">{formik.errors?.long}</div>
                )}
                <div className="flex flex-col w-full gap-2">
                  <label htmlFor="lat" className="">
                    Latitude*
                  </label>
                  <span className=" w-full">
                    <InputText
                      id="lat"
                      name="lat"
                      maxLength={30}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.lat}
                      onChange={formik.handleChange}
                    />
                  </span>
                </div>
                {formik.touched?.lat && formik.errors?.lat && (
                  <div className="p-error">{formik.errors?.lat}</div>
                )}
              </div>
            </div>
          </div>
          <div className="card shadow-md rounded-lg p-4 pt-1 mt-3">
            <Divider>
              <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
                Design Customization
              </span>
            </Divider>

            <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="colorPrimary" className="">
                    Primary Color
                  </label>
                  <span className="flex  w-full gap-2">
                    <InputText
                      id="colorPrimary"
                      name="colorPrimary"
                      maxLength={6}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.colorPrimary}
                      onChange={formik.handleChange}
                    />
                    <div className="p-colorpicker-container   flex ">
                      <ColorPicker
                        id="colorPrimary"
                        format="hex"
                        value={formik.values.colorPrimary}
                        onChange={formik.handleChange}
                        inline={false}
                      />
                    </div>
                  </span>
                  <small id="colorPrimary-help">
                    Enter your color in HEX format (e.g., 1A2B3C)
                  </small>
                </div>
                {formik.touched?.colorPrimary &&
                  formik.errors?.colorPrimary && (
                    <div className="p-error">{formik.errors?.colorPrimary}</div>
                  )}
              </div>

              <div className="">
                <div className=" flex flex-col gap-2">
                  <label htmlFor="colorSecondary" className="">
                    Secondary Color
                  </label>
                  <span className="flex  w-full gap-2">
                    <InputText
                      id="colorSecondary"
                      name="colorSecondary"
                      maxLength={6}
                      className="w-full text-lg p-primary-input"
                      value={formik.values.colorSecondary}
                      onChange={formik.handleChange}
                    />
                    <div className=" p-colorpicker-container flex">
                      <ColorPicker
                        id="colorSecondary"
                        format="hex"
                        value={formik.values.colorSecondary}
                        onChange={formik.handleChange}
                        inline={false}
                      />
                    </div>
                  </span>
                  <small id="colorSecondary-help">
                    Enter your color in HEX format (e.g., 1A2B3C)
                  </small>
                </div>
                {formik.touched?.colorSecondary &&
                  formik.errors?.colorSecondary && (
                    <div className="p-error">
                      {formik.errors?.colorSecondary}
                    </div>
                  )}
              </div>

              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="fontStyle" className="">
                    Font Style
                  </label>
                  <span className="flex  w-full gap-2">
                    <Dropdown
                      id="fontStyle"
                      name="fontStyle"
                      className="w-full text-lg p-primary-input"
                      value={formik.values.fontStyle}
                      onChange={formik.handleChange}
                      options={styleOptions}
                      optionLabel="name"
                      optionValue="id"
                      pt={{
                        root: { className: "w-full" },
                        input: { className: "w-full p-primary-input" },
                        filterIcon: { className: "ml-2" },
                        filterInput: { className: "pl-8" },
                      }}
                    />
                  </span>
                </div>
                {formik.touched?.fontStyle && formik.errors?.fontStyle && (
                  <div className="p-error">{formik.errors?.fontStyle}</div>
                )}
              </div>
              <div className="">
                <div className="flex flex-col gap-2">
                  <label htmlFor="logo" className="">
                    Company Logo
                  </label>
                  <div className=" flex flex-col gap-2">
                    <Button
                      onClick={onFileUpload}
                      type="button"
                      label="Upload"
                      icon="pi pi-plus"
                      className="  p-primary-highlight-btn"
                    />

                    <FileUpload
                      ref={fileUploadRef}
                      mode="basic"
                      name="logo-upload"
                      url="/api/upload"
                      accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                      maxFileSize={3000000}
                      auto
                      onSelect={onFileSelect}
                      style={{ display: "none" }}
                    />
                    <small id="logo-upload-help">
                      Please upload a supported file smaller than 3MB.
                    </small>
                    <div className="flex w-32 h-32 overflow-hidden justify-end">
                      {image ? (
                        <Image
                          src={image}
                          alt="Uploaded Logo"
                          width="128"
                          height="128"
                          preview
                        />
                      ) : (
                        setupDetails?.logo && (
                          <Image
                            src={setupDetails.logoPath + setupDetails.logo}
                            alt="Uploaded Logo"
                            width="128"
                            height="128"
                            preview
                          />
                        )
                      )}
                    </div>
                  </div>
                </div>
                {formik.touched?.logo && formik.errors?.logo && (
                  <div className="p-error">{formik.errors?.logo}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-md rounded-lg p-4 mt-3">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
              Meta Tags & SEO
            </span>
          </Divider>

          <div className="px-4 gap-4 grid grid-cols-1 lg:grid-cols-2">
            {/* Basic Meta Tags */}
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="meta_title" className="">
                  Meta Title
                </label>
                <span className="w-full">
                  <InputText
                    id="meta_title"
                    name="meta_title"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.meta_title}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="meta_author" className="">
                  Meta Author
                </label>
                <span className="w-full">
                  <InputText
                    id="meta_author"
                    name="meta_author"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.meta_author}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="meta_robots" className="">
                  Meta Robots
                </label>
                <span className="w-full">
                  <InputText
                    id="meta_robots"
                    name="meta_robots"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.meta_robots}
                    onChange={formik.handleChange}
                    placeholder="e.g., index, follow"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="og_type" className="">
                  Open Graph Type
                </label>
                <span className="w-full">
                  <InputText
                    id="og_type"
                    name="og_type"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.og_type}
                    onChange={formik.handleChange}
                    placeholder="e.g., website, article"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="og_url" className="">
                  Open Graph URL
                </label>
                <span className="w-full">
                  <InputText
                    id="og_url"
                    name="og_url"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.og_url}
                    onChange={formik.handleChange}
                    placeholder="https://example.com"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="og_site_name" className="">
                  Open Graph Site Name
                </label>
                <span className="w-full">
                  <InputText
                    id="og_site_name"
                    name="og_site_name"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.og_site_name}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="twitter_card" className="">
                  Twitter Card Type
                </label>
                <span className="w-full">
                  <InputText
                    id="twitter_card"
                    name="twitter_card"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.twitter_card}
                    onChange={formik.handleChange}
                    placeholder="e.g., summary, summary_large_image"
                  />
                </span>
              </div>
            </div>
          </div>

          {/* Full Width Fields */}
          <div className="px-4 gap-4 grid grid-cols-1 mt-4">
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="meta_description" className="">
                  Meta Description
                </label>
                <span className="w-full">
                  <InputText
                    id="meta_description"
                    name="meta_description"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.meta_description}
                    onChange={formik.handleChange}
                    placeholder="Enter meta description for search engines"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="meta_keywords" className="">
                  Meta Keywords
                </label>
                <span className="w-full">
                  <InputText
                    id="meta_keywords"
                    name="meta_keywords"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.meta_keywords}
                    onChange={formik.handleChange}
                    placeholder="Enter keywords separated by commas"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="og_title" className="">
                  Open Graph Title
                </label>
                <span className="w-full">
                  <InputText
                    id="og_title"
                    name="og_title"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.og_title}
                    onChange={formik.handleChange}
                    placeholder="Enter Open Graph title for social media sharing"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="og_description" className="">
                  Open Graph Description
                </label>
                <span className="w-full">
                  <InputText
                    id="og_description"
                    name="og_description"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.og_description}
                    onChange={formik.handleChange}
                    placeholder="Enter Open Graph description for social media sharing"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="twitter_title" className="">
                  Twitter Title
                </label>
                <span className="w-full">
                  <InputText
                    id="twitter_title"
                    name="twitter_title"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.twitter_title}
                    onChange={formik.handleChange}
                    placeholder="Enter Twitter title for social media sharing"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="twitter_description" className="">
                  Twitter Description
                </label>
                <span className="w-full">
                  <InputText
                    id="twitter_description"
                    name="twitter_description"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.twitter_description}
                    onChange={formik.handleChange}
                    placeholder="Enter Twitter description for social media sharing"
                  />
                </span>
              </div>
            </div>

            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="schema_org" className="">
                  Schema.org JSON-LD
                </label>
                <span className="w-full">
                  <InputText
                    id="schema_org"
                    name="schema_org"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.schema_org}
                    onChange={formik.handleChange}
                    placeholder="Enter Schema.org JSON-LD markup"
                  />
                </span>
                <small className="text-gray-600">
                  Enter valid JSON-LD markup for structured data (e.g.,
                  Organization, Website, etc.)
                </small>
              </div>
            </div>
          </div>
        </div>



        <div className="mt-8">
          <div className="flex justify-end gap-4">
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-red-btn"
              type="button"
              onClick={() => {
                formik.resetForm();
              }}
            />
            <Button
              type="submit"
              label="Save"
              icon="pi pi-check"
              className="p-secondary-btn"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
